import React, { useEffect, useMemo, useState } from "react";

type CountdownProps = 
  | {
      moveDate: string;
      moveTime?: string;
      deadline?: never;
      variant?: "default" | "compact";
    }
  | {
      deadline: string;
      moveDate?: never;
      moveTime?: never;
      variant?: "default" | "compact";
    };

const CountdownTimer: React.FC<CountdownProps> = ({ 
  moveDate, 
  moveTime, 
  deadline: deadlineProp,
  variant = "default"
}) => {
  // Memoize deadline calculation - only recompute when props change
  const deadline = useMemo(() => {
    // If deadline prop is provided directly, use it
    if (deadlineProp) {
      const deadlineTime = new Date(deadlineProp).getTime();
      // Check if the date is valid
      if (isNaN(deadlineTime)) {
        return null;
      }
      return deadlineTime;
    }
    
    // Otherwise compute from moveDate and moveTime
    if (!moveDate) {
      return null;
    }
    
    // Parse moveDate without timezone conversion
    let baseDate: Date;
    if (typeof moveDate === 'string' && moveDate.includes('T')) {
      const datePart = moveDate.split('T')[0];
      const [year, month, day] = datePart.split('-');
      baseDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      baseDate = new Date(moveDate);
    }
    
    // Check if the date is valid
    if (isNaN(baseDate.getTime())) {
      return null;
    }
    
    if (moveTime) {
      const [hours, minutes] = moveTime.split(":").map(Number);
      baseDate.setHours(hours, minutes, 0, 0);
    } else {
      baseDate.setHours(23, 59, 59, 999);
    }
    return baseDate.getTime();
  }, [moveDate, moveTime, deadlineProp]);

  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    // If deadline is invalid, set empty
    if (!deadline || isNaN(deadline)) {
      setTimeLeft("");
      return;
    }

    const update = () => {
      const diff = deadline - Date.now();
      if (diff <= 0) {
        setTimeLeft("");
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`);
      } else {
        setTimeLeft(`${hours}h ${minutes}m`);
      }
    };

    update(); // Run immediately
    const interval = setInterval(update, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [deadline]);

  if (!timeLeft) {
    return variant === "compact" ? (
      <span className="text-xs text-gray-500">⏱ Expired</span>
    ) : (
      <p className="text-sm text-gray-900 mb-3">Time is up</p>
    );
  }

  return variant === "compact" ? (
    <span className="text-xs text-gray-500">⏱ {timeLeft}</span>
  ) : (
    <p className="text-sm text-gray-900 mb-3">
      Left: {timeLeft}
    </p>
  );
};

export default CountdownTimer;
